import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  BookOpen, 
  Calculator,
  BarChart3,
  ExternalLink,
  Search,
  Database,
  FileText,
  TrendingUp,
  MapPin,
  Briefcase,
  GraduationCap,
  Mail,
  ArrowRight
} from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { generateWebPageSchema, generateOrganizationSchema, generateMultipleSchemas } from '@/lib/schema';
import { LeaderboardAd, InContentAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';
import NewsletterSignup from '@/components/shared/NewsletterSignup';

interface Resource {
  id: string;
  title: string;
  description: string;
  category: 'Tools' | 'Guides' | 'Data Sources' | 'Calculators';
  type: 'internal' | 'external';
  url: string;
  icon?: React.ReactNode;
  tags?: string[];
}

const Resources = () => {
  const baseUrl = 'https://wageatlas.com';
  const pageUrl = `${baseUrl}/resources/`;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const resources: Resource[] = [
    // Tools
    {
      id: '1',
      title: 'Salary Search',
      description: 'Search and compare salaries for any occupation across all U.S. states and metro areas.',
      category: 'Tools',
      type: 'internal',
      url: '/search',
      icon: <Search className="h-5 w-5" />,
      tags: ['Salary', 'Search', 'Comparison']
    },
    {
      id: '2',
      title: 'Wage Trends Analysis',
      description: 'Analyze historical salary trends and growth patterns for occupations over time.',
      category: 'Tools',
      type: 'internal',
      url: '/trends',
      icon: <TrendingUp className="h-5 w-5" />,
      tags: ['Trends', 'Analysis', 'Historical Data']
    },
    {
      id: '3',
      title: 'Best States Tool',
      description: 'Compare salary potential across states for any occupation, adjusted for cost of living.',
      category: 'Tools',
      type: 'internal',
      url: '/best-states',
      icon: <MapPin className="h-5 w-5" />,
      tags: ['Location', 'Comparison', 'RPP']
    },
    {
      id: '4',
      title: 'Industry Analysis',
      description: 'Explore salary data by industry and discover which industries pay the most for your occupation.',
      category: 'Tools',
      type: 'internal',
      url: '/industry',
      icon: <Briefcase className="h-5 w-5" />,
      tags: ['Industry', 'Analysis', 'NAICS']
    },
    // Calculators
    {
      id: '5',
      title: 'Salary Calculator',
      description: 'Convert between hourly and annual salaries, calculate take-home pay, and more.',
      category: 'Calculators',
      type: 'internal',
      url: '/calculators/salary',
      icon: <Calculator className="h-5 w-5" />,
      tags: ['Calculator', 'Conversion', 'Salary']
    },
    {
      id: '6',
      title: 'Tax Calculator',
      description: 'Estimate federal and state taxes on your salary with detailed breakdowns.',
      category: 'Calculators',
      type: 'internal',
      url: '/calculators/tax',
      icon: <Calculator className="h-5 w-5" />,
      tags: ['Tax', 'Calculator', 'Estimate']
    },
    // Guides
    {
      id: '7',
      title: 'SOC Code Guide',
      description: 'Learn about Standard Occupational Classification codes and how to find yours.',
      category: 'Guides',
      type: 'internal',
      url: '/what-is-soc-code',
      icon: <FileText className="h-5 w-5" />,
      tags: ['SOC Code', 'Guide', 'Education']
    },
    {
      id: '8',
      title: 'Methodology',
      description: 'Understand how we calculate salary data, career trends, and labor market insights.',
      category: 'Guides',
      type: 'internal',
      url: '/methodology',
      icon: <BookOpen className="h-5 w-5" />,
      tags: ['Methodology', 'Data', 'Transparency']
    },
    {
      id: '9',
      title: 'No Degree Jobs Guide',
      description: 'Discover high-paying jobs that don\'t require a college degree.',
      category: 'Guides',
      type: 'internal',
      url: '/no-degree-jobs',
      icon: <GraduationCap className="h-5 w-5" />,
      tags: ['No Degree', 'Career Path', 'Education']
    },
    {
      id: '14',
      title: 'How to Read Salary Percentiles',
      description: 'What the 10th-90th percentiles mean, why mean and median disagree, and how to judge any job offer against the full wage distribution.',
      category: 'Guides',
      type: 'internal',
      url: '/insights/how-to-read-salary-percentiles',
      icon: <BookOpen className="h-5 w-5" />,
      tags: ['Percentiles', 'Salary Data', 'Guide']
    },
    {
      id: '15',
      title: 'Cost of Living vs. Salary',
      description: 'How Regional Price Parities work and how to convert any salary into real purchasing power before comparing offers across states.',
      category: 'Guides',
      type: 'internal',
      url: '/insights/cost-of-living-vs-salary',
      icon: <BookOpen className="h-5 w-5" />,
      tags: ['Cost of Living', 'RPP', 'Relocation']
    },
    {
      id: '16',
      title: 'Salary Negotiation Playbook',
      description: 'Turn government wage data into a defensible ask, with scripts for offers, counters, and internal raises.',
      category: 'Guides',
      type: 'internal',
      url: '/insights/salary-negotiation-guide',
      icon: <BookOpen className="h-5 w-5" />,
      tags: ['Negotiation', 'Career Advice', 'Scripts']
    },
    {
      id: '17',
      title: 'Understanding Wage Trends',
      description: 'Nominal vs. real growth, volatility, and what recessions actually do to different occupations\' paychecks.',
      category: 'Guides',
      type: 'internal',
      url: '/insights/understanding-wage-trends',
      icon: <TrendingUp className="h-5 w-5" />,
      tags: ['Trends', 'Wage Growth', 'Recession']
    },
    // Data Sources
    {
      id: '10',
      title: 'Bureau of Labor Statistics',
      description: 'Official source for occupational employment and wage statistics (OEWS) data.',
      category: 'Data Sources',
      type: 'external',
      url: 'https://www.bls.gov/oes/',
      icon: <Database className="h-5 w-5" />,
      tags: ['BLS', 'Government', 'Official Data']
    },
    {
      id: '11',
      title: 'O*NET Online',
      description: 'Comprehensive database of occupational information including skills, knowledge, and abilities.',
      category: 'Data Sources',
      type: 'external',
      url: 'https://www.onetcenter.org/',
      icon: <Database className="h-5 w-5" />,
      tags: ['O*NET', 'Skills', 'Career Info']
    },
    {
      id: '12',
      title: 'Bureau of Economic Analysis',
      description: 'Regional price parities (RPP) data for cost of living adjustments.',
      category: 'Data Sources',
      type: 'external',
      url: 'https://www.bea.gov/data/prices-inflation/regional-price-parities-state-and-metro-area',
      icon: <Database className="h-5 w-5" />,
      tags: ['BEA', 'RPP', 'Cost of Living']
    },
    {
      id: '13',
      title: 'Data Sources Page',
      description: 'Complete list of all data sources used in Wage Atlas calculations.',
      category: 'Data Sources',
      type: 'internal',
      url: '/data-sources',
      icon: <Database className="h-5 w-5" />,
      tags: ['Data Sources', 'Transparency', 'References']
    }
  ];

  const categories = [
    { name: 'All', count: resources.length },
    { name: 'Tools', count: resources.filter(r => r.category === 'Tools').length },
    { name: 'Guides', count: resources.filter(r => r.category === 'Guides').length },
    { name: 'Calculators', count: resources.filter(r => r.category === 'Calculators').length },
    { name: 'Data Sources', count: resources.filter(r => r.category === 'Data Sources').length }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'All' || resource.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Generate structured data
  const webPageSchema = generateWebPageSchema(
    'Career Resources & Tools - Wage Atlas',
    'Comprehensive collection of career resources, salary tools, calculators, guides, and data sources for career research and planning.',
    pageUrl,
    { baseUrl }
  );
  const organizationSchema = generateOrganizationSchema(baseUrl);
  const structuredData = generateMultipleSchemas([webPageSchema, organizationSchema]);


  const handleResourceClick = (resource: Resource) => {
    if (typeof window !== 'undefined' && (window as unknown).gtag) {
      (window as unknown).gtag('event', 'resource_click', {
        event_category: 'engagement',
        resource_title: resource.title,
        resource_category: resource.category,
        resource_type: resource.type,
      });
    }
  };

  return (
    <>
      <SeoHead
        title="Career Resources & Tools - Wage Atlas"
        description="Comprehensive collection of career resources, salary tools, calculators, guides, and data sources for career research and planning."
        canonical={pageUrl}
        schemas={structuredData}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-8">
              {/* Header */}
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <BookOpen className="h-8 w-8 text-primary" />
                  <h1 className="text-3xl font-bold">Career Resources & Tools</h1>
                </div>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Everything you need to research salaries, plan your career, and make informed decisions. 
                  Tools, calculators, guides, and data sources all in one place.
                </p>
              </div>

              {/* Leaderboard Ad */}
              <AdFrame className="mb-8">
                <LeaderboardAd />
              </AdFrame>

              {/* Where to start */}
              <Card>
                <CardHeader>
                  <CardTitle>Where to Start, Based on What You're Trying to Do</CardTitle>
                  <CardDescription>
                    A directory is only useful if you know which door to open first. Here is how we
                    would sequence these resources for the most common situations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h4 className="font-medium text-foreground mb-1">"I just got a job offer — is it good?"</h4>
                    <p>
                      Look up the occupation and state in the <Link to="/search" className="text-primary hover:underline">salary search</Link>,
                      then read <Link to="/insights/how-to-read-salary-percentiles" className="text-primary hover:underline">the percentile guide</Link> to
                      place the offer on the distribution. If the job is in a different city, run it through
                      the <Link to="/insights/cost-of-living-vs-salary" className="text-primary hover:underline">cost-of-living adjustment</Link> before
                      deciding, and the <Link to="/calculators/tax" className="text-primary hover:underline">tax calculator</Link> for take-home pay.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">"I want a raise"</h4>
                    <p>
                      Start with the <Link to="/insights/salary-negotiation-guide" className="text-primary hover:underline">negotiation playbook</Link> —
                      it walks through exactly which numbers to pull and the scripts to deliver them. The
                      supporting evidence comes from your occupation's salary page and its{' '}
                      <Link to="/trends" className="text-primary hover:underline">trend history</Link>.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">"I'm considering a career change"</h4>
                    <p>
                      Use <Link to="/compare-occupations" className="text-primary hover:underline">occupation comparison</Link> to
                      put candidates side by side, check long-run trajectories in the{' '}
                      <Link to="/trends" className="text-primary hover:underline">trends explorer</Link> (read{' '}
                      <Link to="/insights/understanding-wage-trends" className="text-primary hover:underline">the trends guide</Link> first
                      so the charts don't mislead you), and — if you're skipping the degree route — start from{' '}
                      <Link to="/insights/high-paying-jobs-without-a-degree" className="text-primary hover:underline">the no-degree analysis</Link>.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">"I'm relocating"</h4>
                    <p>
                      The <Link to="/best-states" className="text-primary hover:underline">best states tool</Link> ranks
                      every state for your occupation with cost-of-living adjustment built in;{' '}
                      <Link to="/state/california" className="text-primary hover:underline">state pages</Link> show
                      what else pays well in your destination.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 justify-center">
                {categories.map(category => (
                  <Button
                    key={category.name}
                    variant={selectedCategory === category.name ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category.name)}
                  >
                    {category.name} ({category.count})
                  </Button>
                ))}
              </div>

              {/* Resources Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map(resource => (
                  <Card 
                    key={resource.id} 
                    className="hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 text-primary">
                          {resource.icon}
                          <Badge variant="secondary">{resource.category}</Badge>
                        </div>
                        {resource.type === 'external' && (
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <CardTitle className="text-lg mb-2">
                        {resource.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-3">
                        {resource.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {resource.tags.map(tag => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {resource.type === 'internal' ? (
                        <Button 
                          asChild 
                          variant="ghost" 
                          size="sm" 
                          className="w-full"
                          onClick={() => handleResourceClick(resource)}
                        >
                          <Link to={resource.url}>
                            Open Tool <ArrowRight className="ml-2 h-3 w-3" />
                          </Link>
                        </Button>
                      ) : (
                        <Button 
                          asChild 
                          variant="ghost" 
                          size="sm" 
                          className="w-full"
                          onClick={() => handleResourceClick(resource)}
                        >
                          <a href={resource.url} target="_blank" rel="noopener noreferrer">
                            Visit Site <ExternalLink className="ml-2 h-3 w-3" />
                          </a>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredResources.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No resources found matching your search.</p>
                </div>
              )}

              {/* In-Content Ad */}
              <InContentAd />

              {/* Newsletter Signup */}
              <NewsletterSignup 
                source="resources_page"
                title="Stay Updated with Career Resources"
                description="Get notified when we add new tools, calculators, and resources to help with your career research."
              />

              {/* Quick Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Popular Pages
                  </CardTitle>
                  <CardDescription>
                    Explore our most popular tools and resources.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/insights">
                        <TrendingUp className="h-5 w-5 mb-2" />
                        <span className="font-medium">Career Insights</span>
                        <span className="text-xs text-muted-foreground mt-1">Articles & Analysis</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/methodology">
                        <FileText className="h-5 w-5 mb-2" />
                        <span className="font-medium">Our Methodology</span>
                        <span className="text-xs text-muted-foreground mt-1">How We Process Data</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/what-is-soc-code">
                        <BarChart3 className="h-5 w-5 mb-2" />
                        <span className="font-medium">SOC Code Guide</span>
                        <span className="text-xs text-muted-foreground mt-1">Occupation Classification</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="h-auto flex-col py-4">
                      <Link to="/rankings">
                        <Briefcase className="h-5 w-5 mb-2" />
                        <span className="font-medium">Occupation Rankings</span>
                        <span className="text-xs text-muted-foreground mt-1">Highest-Paying Jobs</span>
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Resources;

